import { uid } from '../../../../lib/uid';
import type { InventoryItemOption, StorageFacilityOption } from '../inventoryTypes';
import { LICENSE_PAYMENT_MODE } from '../inventoryTypes';

/**
 * Test data backing the Autogenerate button on the stock forms.
 *
 * The contract is that the primary action enables on the FIRST try, so a new required field on
 * either form must be added here in the same change — nothing fails when it is not, the button
 * just quietly stops working.
 *
 * <b>It resolves the item against the catalog the form already loaded, and against the right
 * HALF of it.</b> A batch needs a quantity item and a unit needs a serialized one; generating
 * the other would hand the form a value its own server refuses, which reads as a bug in the
 * form rather than in the generator. Where the catalog holds nothing usable the generator
 * returns empty ids and the form stays invalid, which is the honest outcome — the page's
 * prerequisite banner is what tells the reader why.
 */

const LOT_PREFIXES = ['LOT', 'BN', 'B'] as const;

const SERIAL_PREFIXES = ['SN', 'ASSET', 'INV'] as const;

const RECEIPT_NOTES = [
  'Delivered against the quarterly order.',
  'Split delivery — second pallet to follow.',
  'Checked in against the packing list.',
  'Counted and shelved on arrival.',
] as const;

/**
 * What a reader writes on an ADJUSTMENT, which is a different event from a delivery.
 *
 * Its own pool rather than sharing {@link RECEIPT_NOTES}: an adjustment that generated
 * "Checked in against the packing list" would describe goods arriving on a record that says
 * the shelf was recounted — the two movements this module is most careful to keep apart.
 */
const COUNT_NOTES = [
  'Quarterly physical count.',
  'Recounted after the shelf was reorganised.',
  'Cycle count — discrepancy against the record.',
  'Verified against the bin card.',
] as const;

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)]!;
}

function suffix(): string {
  return uid().slice(0, 6).toUpperCase();
}

/** A calendar date `n` days from today, as `YYYY-MM-DD`. Never an instant. */
function inDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

export function sampleStockBatch(catalogs: {
  items: InventoryItemOption[];
  facilities: StorageFacilityOption[];
}): {
  inventoryItemId: string;
  storageFacilityId: string;
  batchNumber: string;
  quantity: string;
  expiryDate: string;
  unitCost: string;
  notes: string;
} {
  // A serialized item is recorded as units, one row per thing — the server refuses a lot of
  // one, so the generator never offers it.
  const offered = catalogs.items.filter((i) => !i.tracksSerial);
  const item = offered.length > 0 ? pick(offered) : null;
  const facility = catalogs.facilities.length > 0 ? pick(catalogs.facilities) : null;

  return {
    inventoryItemId: item?.id ?? '',
    storageFacilityId: facility?.id ?? '',
    batchNumber: `${pick(LOT_PREFIXES)}-${new Date().getFullYear()}-${suffix()}`,
    quantity: String(20 + Math.floor(Math.random() * 480)),

    /*
      A date only where the ITEM tracks expiry, and always in the FUTURE. A lapsed date is a
      legitimate thing to record — stock does arrive expired — but a generator producing one
      would make every generated lot read as Expired the moment it saved, which exercises the
      wrong state and looks like a defect.
    */
    expiryDate: item?.tracksExpiry ? inDays(180 + Math.floor(Math.random() * 540)) : '',

    // Two decimals, because the column is decimal(18,2) and a value the server rounds is one
    // the reader cannot reconcile with what they typed.
    unitCost: (Math.round((15 + Math.random() * 900) * 100) / 100).toFixed(2),

    notes: pick(RECEIPT_NOTES),
  };
}

/**
 * An ADJUSTMENT against an item the reader has ALREADY chosen by opening the form on its row.
 *
 * <b>It takes the item rather than picking one</b>, which is the "never a field the reader has
 * already decided" rule: the item is what the kebab settled, and a generator that re-picked it
 * would move the form off the row it was opened from.
 *
 * <b>It FILLS the unit cost, and that reverses what this comment used to say.</b> The old
 * version read "it does not fill the unit cost either… the form seeds it from the catalog's own
 * reference price… the cost is optional here by design", and all three halves of that stopped
 * being true: the catalog's reference price was dropped on 20 Sep 2026, so nothing seeds the
 * field, and the cost became REQUIRED on this form on 21 Sep. A required field the generator
 * leaves empty is an Autogenerate that silently stops enabling the primary action — which is
 * the one thing it exists to prevent.
 */
export function sampleAdjustment(item: { tracksExpiry: boolean }): {
  batchNumber: string;
  quantity: string;
  expiryDate: string;
  unitCost: string;
  notes: string;
} {
  return {
    // Suffixed, because Autogenerate is most useful clicked repeatedly — and here an unsuffixed
    // number would resolve onto the lot the last click created and silently CORRECT it rather
    // than opening a new one.
    batchNumber: `${pick(LOT_PREFIXES)}-${new Date().getFullYear()}-${suffix()}`,
    quantity: String(20 + Math.floor(Math.random() * 480)),

    // In the FUTURE, for the reason the batch generator's is: a lapsed date is legitimate to
    // record and a generated one would make every generated lot read as Expired on save. Only
    // READ where the adjustment opens a lot — an existing one keeps the date it arrived with.
    expiryDate: item.tracksExpiry ? inDays(180 + Math.floor(Math.random() * 540)) : '',

    // Two decimals, because the column is decimal(18,2) and a value the server rounds is one
    // the reader cannot reconcile with what they typed.
    unitCost: (Math.round((15 + Math.random() * 900) * 100) / 100).toFixed(2),

    notes: pick(COUNT_NOTES),
  };
}

/**
 * What a unit was bought under, printed in the ICS / PAR "Fund" column.
 *
 * Plausible vocabulary rather than obviously synthetic: an appropriation line names nobody
 * and never leaves `rsys_`, so the rule `samplePersonnel` runs on does not bind — the test
 * is where the value LANDS, not which generator produced it.
 */
const FUNDS = ['General Fund', 'Trust Fund', 'Special Education Fund', 'Capital Outlay'];

export function sampleStockUnit(catalogs: {
  items: InventoryItemOption[];
  facilities: StorageFacilityOption[];
}): {
  inventoryItemId: string;
  storageFacilityId: string;
  serialNumber: string;
  acquisitionDate: string;
  fund: string;
  unitCost: string;
  notes: string;
} {
  // The mirror of the batch generator: only an item whose stock is NAMED one row at a time.
  const offered = catalogs.items.filter((i) => i.tracksSerial);
  const item = offered.length > 0 ? pick(offered) : null;
  const facility = catalogs.facilities.length > 0 ? pick(catalogs.facilities) : null;

  return {
    inventoryItemId: item?.id ?? '',
    storageFacilityId: facility?.id ?? '',

    // Suffixed, because Autogenerate is most useful clicked repeatedly and a serial is held
    // once per item — without it the second click is a duplicate the server refuses.
    serialNumber: `${pick(SERIAL_PREFIXES)}-${suffix()}${suffix().slice(0, 2)}`,

    // In the PAST: a unit is acquired before it is received, and a future acquisition is the
    // one value here a reader would read as wrong.
    acquisitionDate: inDays(-(30 + Math.floor(Math.random() * 700))),

    fund: pick(FUNDS),

    unitCost: (Math.round((3000 + Math.random() * 90000) * 100) / 100).toFixed(2),

    notes: 'Received in working order.',
  };
}

/**
 * ONE ROW of the registration grid — the serialized half of the batch generator, per row.
 *
 * It takes the FACILITIES and not the item catalog, because the registration form does not ask
 * which item: the reader chose it by opening a row's kebab, and the form states it as a
 * read-only fact. Generating one would overwrite a value the reader already decided.
 *
 * The serial is SUFFIXED twice over — the generator is called once per row and Autogenerate is
 * most useful clicked repeatedly, so without it the second row of the first click is a
 * duplicate the form refuses before the request is even sent.
 */
/**
 * Plausible hire companies — an ORGANIZATION, so the obviously-synthetic rule does not bind.
 *
 * That rule is about a value that LANDS in `rsys_pii`, where a fabricated-but-realistic name is
 * indistinguishable from a real one to anyone auditing it later. A lessor is a column on
 * `StockUnits` and never leaves `rsys_`, which is the same reading `sampleEducation` takes for a
 * school and the issuance generator takes for an external party.
 */
const LESSORS = [
  'Northgate Equipment Leasing',
  'Vanguard Fleet Rentals',
  'Summit Asset Hire',
  'Meridian Plant & Machinery',
  'Crosspoint Leasing Services',
];

export function sampleStockUnitRow(facilities: StorageFacilityOption[]): {
  storageFacilityId: string;
  serialNumber: string;
  acquisitionDate: string;
  fund: string;
  leaseEndDate: string;
  leasedFrom: string;
  unitCost: number;
  notes: string;
} {
  const facility = facilities.length > 0 ? pick(facilities) : null;

  return {
    storageFacilityId: facility?.id ?? '',

    serialNumber: `${pick(SERIAL_PREFIXES)}-${suffix()}${suffix().slice(0, 2)}`,

    // In the PAST: a unit is acquired before it is received, and a future acquisition is the
    // one value here a reader would read as wrong.
    acquisitionDate: inDays(-(30 + Math.floor(Math.random() * 700))),

    /*
      FILLED, though the TENURE TICK stays where it is — the two halves of the flag rule. A
      generator never moves a control that reveals a block, because the same button would then
      produce a different form on each click; it fills what sits BEHIND it, so one untick
      reveals a filled section rather than two empty columns.

      Nothing is refused by leaving them on an OWNED registration: the form clears both in the
      payload where the tick says owned, exactly as the server does on write.

      The lessor is plausible vocabulary rather than an obviously synthetic name, because it is
      an ORGANIZATION and never reaches `rsys_pii` — the test is where the value LANDS.
    */
    leaseEndDate: inDays(90 + Math.floor(Math.random() * 900)),
    leasedFrom: pick(LESSORS),

    fund: pick(FUNDS),

    // A NUMBER, not a string: the grid cell is `<TdMoney>`, which holds `number | null`.
    unitCost: Math.round((3000 + Math.random() * 90000) * 100) / 100,

    notes: 'Received in working order.',
  };
}
/**
 * ONE ROW of the LICENCE registration grid.
 *
 * It takes the STAFF list rather than the facilities, because that form scopes the facility to
 * the header — a licence has no shelf, so one store covers the whole registration and a row has
 * no such field to fill. The item is stated rather than asked for, exactly as it is on the
 * serialized form.
 *
 * <b>The licence number is SUFFIXED twice over</b>, for the reason a serial is: the generator is
 * called once per row and Autogenerate is most useful clicked repeatedly, so without it the
 * second row of the first click is a duplicate the form refuses before the request is sent.
 *
 * <b>It fills no name and reaches `rsys_pii` not at all.</b> The accountable employee is picked
 * from the live list this form already loaded — resolving against the real catalog rather than
 * inventing an id, which is the generator rule that keeps a generated form submittable. Where
 * the reader cannot read personnel there is no list, so the cell is left empty rather than
 * seeded with an id the picker cannot label.
 *
 * <b>The TERM END is filled only where the item is licensed for a term</b>, because the column
 * is not drawn otherwise — a value behind a cell nobody can see is one the reader cannot correct.
 */
export function sampleLicenseRow(
  staff: { id: string; name: string }[],
  tracksTerm: boolean,
): {
  licenseNumber: string;
  acquisitionDate: string;
  termEndDate: string;
  accountablePersonnelId: string;
  unitCost: number;
  paymentMode: string;
  firstBillingDate: string;
} {
  /*
    A RECURRING mode, always — never One-time. The first-billing cell is disabled beside a
    one-time payment, so generating one would leave the reader looking at a dead column and
    wondering whether the feature works. The contract is that the primary action enables on the
    first try, and it does either way; what this buys is that every cell the grid draws is
    exercised.
  */
  const recurring = [
    LICENSE_PAYMENT_MODE.monthly,
    LICENSE_PAYMENT_MODE.quarterly,
    LICENSE_PAYMENT_MODE.semiAnnual,
    LICENSE_PAYMENT_MODE.annual,
  ];

  const mode = recurring[Math.floor(Math.random() * recurring.length)]!;

  return {
    licenseNumber: `LIC-${suffix()}${suffix().slice(0, 2)}`.toUpperCase(),

    // In the PAST: a licence is bought before it is recorded, and a future acquisition is the
    // one value here a reader would read as wrong.
    acquisitionDate: inDays(-(15 + Math.floor(Math.random() * 400))),

    // FORWARD, and only on a term licence — a term that has already lapsed is a seat nobody
    // would register today, which is the one value on this row that points ahead by design.
    termEndDate: tracksTerm ? inDays(90 + Math.floor(Math.random() * 640)) : '',

    accountablePersonnelId: staff.length > 0 ? pick(staff).id : '',

    // A NUMBER, not a string: the grid cell is `<TdMoney>`, which holds `number | null`.
    unitCost: Math.round((500 + Math.random() * 25000) * 100) / 100,

    // The wire carries the enum as an int, and the cell holds the string the picker offers.
    paymentMode: String(mode),

    // Ahead of today, because a schedule that starts is what a first billing date says.
    firstBillingDate: inDays(5 + Math.floor(Math.random() * 60)),
  };
}

/*
  NO generator here fills OWNERSHIP or a lease end date, and that is the step-adding-flag rule
  rather than an omission: ownership reveals the lease field, so choosing one would make the same
  button produce a different form on each click.

  It read "NEITHER" while there were two of them and there are four from 23 Sep 2026 — a count in
  a comment is a sentence the next generator makes untrue.

  `sampleStockUnitRow` could not fill ownership in any case: the registration form scopes tenure
  to the HEADER, so a row has no such field to fill. `sampleLicenseRow` has no ownership to fill
  at all — a licence's tenure is DERIVED from how the item is licensed, so that form neither asks
  for it nor sends one.

  A TERM END is the one exception, and only on the licence row: it is drawn per seat there
  whenever the item is licensed for a term, so leaving it empty would leave a column the reader
  can see and the generator never exercises.
*/
